import { CartItem, CartModifierGroup } from '../../../utils/cart';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { checkItemInCartItemTree } from '../../../utils/menu';
import { getModGroupDisplayName } from '../components/ModGroupLabel';

/**
 * HITL-1616 — everything a collapsed group row needs to say, derived in one
 * place so the singular and multiple variants cannot describe the same cart
 * differently.
 */
export interface ModGroupRowModel {
  /** The group's own name — no `[parent]` prefix, no `!`, no `(select …)`. */
  label: string;
  /** Selected option names, in the order the cart holds them. */
  selectedNames: string[];
  /** `Vanilla, Caramel` — the value cell's text. Empty when nothing is chosen. */
  value: string;
  /** How many are chosen; the count chip reads this. */
  selectedCount: number;
  /** Required and not satisfied — the row prints `! Choose N`. */
  isUnmet: boolean;
  /** `Choose 2` etc., for the unmet case. */
  unmetText: string;
}

export const buildModGroupRowModel = (
  modGroup: ParsedModifierGroup,
  cartItem: CartItem,
  pathToItem: string
): ModGroupRowModel => {
  const cartModGroup = checkItemInCartItemTree({
    cartItem,
    pathToItem,
  }) as CartModifierGroup | null;

  const selectedNames = Object.values(cartModGroup?.selectedItems ?? {}).map(
    (item) => item.name
  );
  const selectedCount = selectedNames.length;
  const minimum = modGroup.minimumSelections ?? 0;

  return {
    label: getModGroupDisplayName(modGroup),
    selectedNames,
    value: selectedNames.join(', '),
    selectedCount,
    isUnmet: minimum > 0 && selectedCount < minimum,
    unmetText: `Choose ${minimum}`,
  };
};

/**
 * Does picking this option reveal further groups? The drawer marks those with a
 * `›` so the agent knows the row is a doorway rather than a leaf.
 *
 * A presence check, deliberately NOT `sortModGroups`. That helper reads
 * `cartItem.sortOrder.reduce(...)` unguarded (`utils/menu.ts:1092`), and an
 * unselected option in a modifier group need not carry a `sortOrder` —
 * `RecursiveModGroup` only ever calls it for options the cart already holds.
 * Ordering is irrelevant here anyway; the only question is whether there is
 * anything below.
 */
export const optionBringsFurtherGroups = (option: ParsedMenuItem): boolean =>
  Object.keys(option?.modifierGroups ?? {}).length > 0;
